import { FC } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import AbsenceGroupsEmpty from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsEmpty";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import { PresenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IAbsenceGroup } from "./AbsenceGroups.models";

interface IAbsenceGroupsListProps {
  data: IAbsenceGroup[];
  isFetching: boolean;
}

const AbsenceGroupsList: FC<IAbsenceGroupsListProps> = ({
  data = [],
  isFetching,
}) => {
  const history = useHistory();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );

  if (isEmpty(data) && !isFetching) {
    return <AbsenceGroupsEmpty />;
  }

  return (
    <Box sx={{ bgcolor: "grey.50", p: "2rem", borderRadius: "1rem" }}>
      <List data-testid="absence-groups-list" disablePadding>
        {(isFetching ? Array.from(new Array(3)) : data).map((item, index) => (
          <ListItem
            key={item?.id || index}
            divider
            sx={{
              p: "1rem 0",
            }}
            data-testid="absence-groups-list__item"
          >
            <Stack direction="row" sx={{ width: "100%" }}>
              {item ? (
                <>
                  <BookmarkIcon
                    sx={{
                      fontSize: "2rem",
                      color: item.color,
                      margin: "0.25rem 1rem 1rem 0",
                    }}
                  />
                </>
              ) : (
                <Skeleton variant="rectangular" width={78} height={64} />
              )}
              {item ? (
                <>
                  <Box width="75%">
                    <Typography
                      variant="subtitle1"
                      component="h4"
                      data-testid="absence-groups-list__item-name"
                    >
                      {item.name}
                    </Typography>
                    {item.presenceType && (
                      <Box sx={{ mt: "0.5rem" }}>
                        <Typography
                          variant="body2"
                          component="span"
                          data-testid="absence-group-presence-type"
                        >
                          {item.presenceType === PresenceType.Absence &&
                            __("Nieobecność")}
                          {item.presenceType === PresenceType.Attendance &&
                            __("Status obecności")}
                          <span style={{ margin: "0 0.5rem" }}>&bull;</span>
                          {__("Przypisane wnioski:")}{" "}
                          {item.assignedAbsenceTypesCount}
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      mt="0.875rem"
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box width="75%" ml="1rem">
                  <Skeleton width="40%" />
                  <Skeleton />
                </Box>
              )}
              {canWriteAbsencesConfiguration && (
                <Box
                  sx={{
                    flexShrink: 0,
                    marginLeft: "auto",
                  }}
                >
                  <ActionButton
                    label={__("Edytuj")}
                    bgColor="info.main"
                    bgColorHover="info.dark"
                    color="white"
                    loading={isFetching}
                    margin="0.5rem"
                    onClick={() =>
                      history.push(
                        `${basePath}/absences/absence-groups/${item.id}`
                      )
                    }
                    data-testid="absence-groups-list__button-edit"
                  >
                    <EditNoteOutlinedIcon />
                  </ActionButton>
                </Box>
              )}
            </Stack>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default AbsenceGroupsList;
